import type { ReportTypeId } from './reportTypes';

/** Shared by generators and read-side currency; no prompt-body imports in decision code. */
export const REPORT_PROMPT_VERSIONS = Object.freeze({
  // 10 Oct follow-through: feasibility/power/test-fit public grid context no
  // longer implies site-offered capacity, a project queue position or timing.
  // 10 Oct: all report types consume shared SED electricity scope. State EIA
  // averages retain state/year and cannot establish tariffs or BTM breakeven.
  // Applicable tariff terms require their own evidence; legacy prices stay generic.
  // 8 Oct recovery: all types change because source-quoted water labels bind
  // to source roles, and applied directives retain scoped contradiction flags.
  // Also covers current-only synthesis, Custom Brief intent currency, zoning
  // floors, water-invalid max-power withholding and regulatory provenance.
  // Financial/Supply Chain add structured model and BOM precedence context.

  // Stage 1 supports conditional architecture/size recommendations from qualified
  // source/model bounds; grid letters constrain grid certainty, not BTM screening.
  // C2 (2026-09-29): shared phase directive labels non-exact phase timing as a planning
  // target (not a COD) and carries the declared IT-load basis; the intent stamp reads
  // "<N> MW cumulative IT (<n> phases)"; editorial re-voicing keeps quantity role nouns;
  // the NWI canonical fact line says "full source-feature total, not parcel acreage".
  // Feasibility and environmental also add type-specific source-role rules.
  // The same contracts also ship non-prompt changes: grounding-validator-v2 non-phase
  // bindings (B; phase-number binding unchanged), environmental NAAQS attainment
  // "Not verified" scoring with the air-attainment-truth-v1 analysisPolicy suffix (E),
  // corrected fallback power and gas-010 DAM text (F), and the gemini-3.1-pro-preview /
  // gemini-3.8-flash model flip. See src/services/promptChangelog.md.
  // Shared load/review context keeps PUE and electrical power factor independent;
  // unknown PF withholds MVA without erasing the declared IT/facility conversion.
  // Source-backed design margins and saved annual-model budgets retain their
  // separate frozen roles; neither populates utility or generation/PPA facts.
  // Accepted customer load service retains its own scope and delivery milestones;
  // it does not establish generator LGIA approval or completed commissioning.
  // v36 (F2): structured-model precedence block (modeled CAPEX, billing months) only when
  // the frozen data-center model's own text disagrees with it. The same v36 also covers
  // the conflict-only BOM spec-precedence section in the shared supply-chain block.
  // v37 (release integration): v36 plus the R12 islanded-phase BTM anchor wording; lane G
  // had taken v36 in parallel, so both contracts ship under this one number.
  'financial': '2026-10-10-electricity-evidence-scope-v39',
  // EPA NAAQS designations: the SED ENVIRONMENTAL block carries the Green Book county designation (environmental, feasibility).
  // EPA-7 (2026-09-30): the same block states where EPA's nonattainment-area boundaries place the site point
  // for part-county areas (inside / outside, or the unchanged Part 81 caveat when unresolved).
  // Grid-only scenario prompts distinguish feed envelope, surviving supply and source allocation;
  // deterministic ELCC score copy describes modeled credit, never an authored target or utility commitment.
  // R12 (2026-10-06): feasibility, environmental, power-availability, executive-summary and
  // public-sentiment embed the general-US regulatory feasibility doctrine (four statuses);
  // feasibility also receives the frozen screen block, and islanding is never inferred.
  // All eleven advance because the shared islanded-phase BTM commercial anchor no longer
  // equates behind-the-meter with no grid import.
  // R11: feasibility v68, environmental v58, interconnection v42 and executive-summary v35
  // also carry the corrected KB env-003 / gas DAM text; no separate R11 bump is needed.
  // The gas-004 / gas-009 'louisiana' retag leaves the water-resource, test-fit,
  // supply-chain-risk and public-sentiment DAM pools; it rides v43 / v46 / v38 / v41.
  // Release integration (2026-10-06): a type bumped by more than one lane takes the next
  // free number covering every lane's contract (see src/services/promptChangelog.md).
  'feasibility': '2026-10-10-grid-evidence-scope-v71',
  'environmental': '2026-10-10-electricity-evidence-scope-v60',
  // W7 (non-prompt contract, v47): the Water Duty projection withholds headroom/adequacy when its
  // declared limit or cooling rows equal a whole-site SED source quantity, or the subtotal is zero.
  // v48: W7 plus the R12 regulatory feasibility doctrine (lane G's parallel v47).
  'power-availability': '2026-10-10-grid-evidence-scope-v51',
  // R11 (lane F v42): corrected KB env-003 (potential to emit, no technology exemption) and gas
  // DAM baselines gas-005/gas-008/gas-030 (source aggregation; islanding avoids only the grid
  // interface). v43: R11 plus the R12 islanded-anchor wording (lane G's parallel v42).
  'water-resource': '2026-10-10-electricity-evidence-scope-v45',
  // W8 (v45): water-role author rule; WATER-TRUTH-1 source-role audit and bounded water revision focus.
  // v46: W8 plus the R12 islanded-anchor wording (lane G's parallel v45).
  'test-fit': '2026-10-10-grid-evidence-scope-v49',
  'interconnection': '2026-10-10-electricity-evidence-scope-v44',
  // v37 (F3): conflict-only BOM spec precedence (a free-text description never overrides the
  // recorded spec/quote kV ratio). v38: F3 plus the R12 islanded-anchor wording (lane G's
  // parallel v37).
  'supply-chain-risk': '2026-10-10-electricity-evidence-scope-v40',
  // Includes deterministic evidence tables (psr-meta-v2) for assessed scans.
  // v41: R11 KB text (lane F v40) plus the R12 regulatory feasibility doctrine (lane G v40).
  'public-sentiment': '2026-10-10-electricity-evidence-scope-v43',
  // v39: R11 KB text (lane F v38) plus the R12 islanded-anchor wording (lane G v38).
  'custom-brief': '2026-10-10-electricity-evidence-scope-v41',
  'executive-summary': '2026-10-10-electricity-evidence-scope-v37',
}) satisfies Readonly<Record<ReportTypeId, string>>;
